import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { CategoryData, LearnedWord, WordData } from '../../types/types';
import { shuffleArray } from '../../utils/utils';
import { useTheme } from '../ThemeContext';
import FloatingBackButton from '../FloatingBackButton';
import SummaryScreen from '../SummaryScreen';
import { getWordTranslation } from '../../utils/localizedContent';
import './HieroglyphCard.css';

// Mirrors LearnMode.tsx's mastery test (TESTS_PER_WORD/XP_PER_WORD, same
// values) so Kama's XP/accuracy finally reflect real recall instead of the
// flat 100% previously credited just for flipping through the book — but
// the question itself is shown as a Kama-style card (reusing HieroglyphCard.
// css's .hgw__card/.hgw__quiz-* classes) rather than LearnMode's lm-arch-card.
const TESTS_PER_WORD = 3;
const XP_PER_WORD = 10;
const ADVANCE_DELAY_MS = 1000;

interface KamaWordTestProps {
  categoryLabel: string;
  // Already filtered to real vocabulary by the caller (no section headers,
  // no narrative/methodology entries) — every word here gets tested.
  words: [string, WordData][];
  // Fallback pool for distractor translations when `words` has fewer than
  // 4 entries (mirrors LearnMode.generateStableOptions's category-wide pad).
  fullCategoryData: CategoryData;
  onExit?: () => void;
  onComplete: (masteredWords: LearnedWord[]) => void;
}

const KamaWordTest: React.FC<KamaWordTestProps> = ({
  categoryLabel,
  words,
  fullCategoryData,
  onExit,
  onComplete,
}) => {
  const { theme } = useTheme();
  const { t, i18n } = useTranslation();
  const wordMap = useMemo(() => new Map(words), [words]);

  const [queue, setQueue] = useState<string[]>([]);
  const [totalQuestions, setTotalQuestions] = useState(0);
  const [options, setOptions] = useState<string[]>([]);
  const [selectedOption, setSelectedOption] = useState<number | null>(null);
  const [phase, setPhase] = useState<'testing' | 'summary'>('testing');
  const scoresRef = useRef<Record<string, number>>({});
  const [scores, setScores] = useState<Record<string, number>>({});
  const completedRef = useRef(false);

  // Localizing here (rather than at render) keeps the question, the answer
  // buttons and the correction panel all consistent with the same value —
  // whichever language is active, they can never disagree with each other.
  const generateOptions = (key: string): string[] => {
    const correct = getWordTranslation(wordMap.get(key) ?? { translation: '' }, i18n.language);
    // Different Kama entries can share the same gloss (e.g. two verb forms
    // both translating to "Danser") — deduplicating the whole session+
    // category pool up front (rather than only the fullCategoryData
    // fallback, as before) is what stops the same translation from being
    // picked as two separate wrong options.
    const candidatePool = [
      ...words.filter(([k]) => k !== key).map(([, data]) => getWordTranslation(data, i18n.language)),
      // Chapter titles and end-of-chapter reviews aren't words — their
      // glosses ("ꜣ · ỉ · y · ꜥ · w — cinq sons…", "Retrouver le signe à
      // partir du son") would be giveaway wrong options.
      ...Object.values(fullCategoryData)
        .filter((data) => !data.isSectionHeader && !data.isChapterReview)
        .map((data) => getWordTranslation(data, i18n.language)),
    ];
    const uniqueWrong = Array.from(
      new Set(candidatePool.filter((t): t is string => Boolean(t) && t !== correct)),
    );
    const wrongOptions = shuffleArray(uniqueWrong).slice(0, 3);

    return shuffleArray([correct, ...wrongOptions]);
  };

  // (Re)build the test whenever the word list changes (new category, or a
  // fresh attempt at the same one) or the interface language changes.
  useEffect(() => {
    const initialQueue = shuffleArray(words.flatMap(([key]) => Array(TESTS_PER_WORD).fill(key)));
    scoresRef.current = {};
    completedRef.current = false;
    setScores({});
    setSelectedOption(null);
    setPhase('testing');
    setTotalQuestions(initialQueue.length);
    setQueue(initialQueue);
    if (initialQueue.length > 0) setOptions(generateOptions(initialQueue[0]));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [words, i18n.language]);

  const currentKey = queue[0];
  const currentWord = currentKey ? wordMap.get(currentKey) : undefined;
  const correctTranslation = currentWord ? getWordTranslation(currentWord, i18n.language) : '';

  // The reveal panel (Correct!/Pas tout à fait + the right answer) is
  // appended below the 4 answer buttons, so on a short viewport it renders
  // past the fold — the page itself scrolls fine, but nothing scrolled it
  // into view automatically, so it looked like feedback wasn't showing at
  // all rather than just being below the visible area.
  const revealRef = useRef<HTMLDivElement | null>(null);
  useEffect(() => {
    if (selectedOption !== null) {
      revealRef.current?.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
    }
  }, [selectedOption]);

  const handleAnswer = (optionIndex: number) => {
    if (selectedOption !== null || !currentKey) return;
    setSelectedOption(optionIndex);

    if (options[optionIndex] === correctTranslation) {
      scoresRef.current[currentKey] = (scoresRef.current[currentKey] || 0) + 1;
    }

    setTimeout(() => {
      setSelectedOption(null);
      const nextQueue = queue.slice(1);
      if (nextQueue.length === 0) {
        setScores({ ...scoresRef.current });
        setPhase('summary');
      } else {
        setQueue(nextQueue);
        setOptions(generateOptions(nextQueue[0]));
      }
    }, ADVANCE_DELAY_MS);
  };

  // Nothing testable — shouldn't normally be reachable (the caller only
  // renders this component when there's real vocabulary), but bail out to a
  // no-op completion rather than showing an empty test screen. Declared
  // unconditionally (with the other hooks above) so this early-out never
  // shifts the hook count between renders.
  useEffect(() => {
    if (words.length === 0 && !completedRef.current) {
      completedRef.current = true;
      onComplete([]);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [words]);

  if (words.length === 0) {
    return null;
  }

  if (phase === 'summary') {
    const masteredEntries = Object.entries(scores).filter(([, s]) => s >= TESTS_PER_WORD);
    const totalScore = Object.values(scores).reduce((sum, s) => sum + s, 0);
    const maxScore = words.length * TESTS_PER_WORD;
    const accuracy = maxScore > 0 ? Math.round((totalScore / maxScore) * 100) : 0;
    const xpGained = masteredEntries.length * XP_PER_WORD;

    const finish = () => {
      if (completedRef.current) return;
      completedRef.current = true;
      const masteredWords: LearnedWord[] = masteredEntries.map(([key]) => ({
        word: key,
        category: categoryLabel,
        language: 'ka',
        timestamp: Date.now(),
        translation: wordMap.get(key)?.translation || '',
      }));
      onComplete(masteredWords);
    };

    return (
      <SummaryScreen
        theme={theme}
        label={t('kamaWordTest.testLabel')}
        congrats={{
          text: t('kamaWordTest.testFinished'),
          translation: t('kamaWordTest.wordsMastered', { count: masteredEntries.length, total: words.length, category: categoryLabel }),
        }}
        stats={[
          { value: `+${xpGained}`, label: 'XP' },
          { value: `${masteredEntries.length}/${words.length}`, label: t('kamaWordTest.mastered') },
          { value: `${accuracy}%`, label: t('kamaWordTest.precision') },
        ]}
        // No `children` passed here on purpose: SummaryScreen treats a
        // truthy `children` as "this result has a detail page" and makes its
        // primary "Terminer" button open that page instead of calling
        // primaryBtn.onClick — a second, identically-labelled "Terminer" on
        // that detail page is the one that actually finishes. That two-step
        // click was a minor, easy-to-miss quirk when Kama only tested once
        // at the very end of a category, but now that a test interrupts
        // browsing every WORDS_PER_TEST_BATCH words (see KamaLearnMode.tsx),
        // missing the second click (e.g. exiting after the first "Terminer"
        // thinking the batch is done) forfeits that whole batch — Kama has
        // no in-progress persistence to resume from, unlike LearnMode.tsx.
        // Keeping this single-page summary makes "Terminer" always finish.
        primaryBtn={{ label: t('kamaWordTest.finish'), onClick: finish }}
        onBack={onExit}
      />
    );
  }

  const answered = selectedOption !== null;
  const isCorrect = answered && options[selectedOption] === correctTranslation;
  const questionNumber = totalQuestions - queue.length + 1;

  return (
    <div className="kama-lesson">
      <div className="kama-lesson__header">
        <span className="kama-lesson__category">{t('kamaWordTest.categoryTest', { category: categoryLabel })}</span>
      </div>
      {onExit && <FloatingBackButton onBack={onExit} position="top-left" variant="minimal" />}

      <div className="kama-lesson__progress">
        <div className="kama-lesson__progressbar-track">
          <div
            className="kama-lesson__progressbar-fill"
            style={{ width: `${(Math.min(questionNumber, totalQuestions) / totalQuestions) * 100}%` }}
          />
        </div>
        <span className="kama-lesson__progress-count">
          {Math.min(questionNumber, totalQuestions)} / {totalQuestions}
        </span>
      </div>

      <div className="hgw">
        <div className="hgw__card hgw__card--quiz" style={{ cursor: 'default' }}>
          <span className="hgw__glyphs">{currentWord?.illustration}</span>
          <span className="hgw__translit">{currentKey}</span>
          <p className="hgw__quiz-question">{t('kamaWordTest.whatIsTranslation')}</p>
          <div className="hgw__quiz-options">
            {options.map((option, i) => {
              let stateClass = '';
              if (answered && option === correctTranslation) stateClass = 'is-correct';
              else if (answered && i === selectedOption) stateClass = 'is-incorrect';
              return (
                <button
                  key={`${option}-${i}`}
                  type="button"
                  className={`hgw__quiz-option ${stateClass}`}
                  disabled={answered}
                  onClick={() => handleAnswer(i)}
                >
                  {option}
                </button>
              );
            })}
          </div>
        </div>

        {answered && (
          <div className="hgw__reveal" ref={revealRef}>
            <div className="hgw__reading">
              <p className={`hgw__reading-label ${isCorrect ? 'is-correct' : 'is-incorrect'}`}>
                {isCorrect ? t('kamaWordTest.correct') : t('kamaWordTest.notQuite')}
              </p>
              <p className="hgw__quiz-answer">
                <span className="hgw__translit">{currentKey}</span> —{' '}
                <span className="hgw__translation">{correctTranslation}</span>
              </p>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default KamaWordTest;
